import { fetchPutResponse } from "./ApiUtilities";
import { parseDateOnlyAsLocal } from "./CommonUtilities";

const setFilterData = (
  data,
  setFilterCategory,
  setFilterCountries,
  setFilterComapnies
) => {
  const companies = Object.values(data).map((entry) => [
    entry.company_name,
    entry.isin,
  ]);
  const countries = Array.from(
    new Set(Object.values(data).map((entry) => entry.country_name))
  );
  if (Object.keys(data).length > 0) {
    const categories = Object.keys(Object.values(data)[0].scores);
    setFilterCategory(categories);
  }
  setFilterCountries(countries);
  setFilterComapnies(companies);
};

export const updateChartData = (
  data,
  selectedScoreType,
  setChartData,
  { selectedCompany = [], selectedCountries = [] },
  setIsLoading
) => {
  const selectedCompaniesList = selectedCompany.map((company) => company[0]);
  const selectedCountriesList = selectedCountries;
  try {
    setIsLoading(true);
    const seriesData = Object.values(data) // Use Object.values for direct access to values
      .map((entry) => ({
        name: entry.company_name,
        country: entry.country_name,
        isin: entry.isin,
        data: entry.scores[selectedScoreType].map((score, index) => [
          parseDateOnlyAsLocal(entry.dates[index]).getTime(),
          score === "None" ? null : Number(score),
          entry.isin,
        ]),
      }))
      .filter(
        ({ name, country }) =>
          (selectedCompaniesList.length === 0 ||
            selectedCompaniesList.includes(name)) &&
          (selectedCountriesList.length === 0 ||
            selectedCountriesList.includes(country))
      );
    setChartData(seriesData);
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    setIsLoading(false);
  }
};

export const fetchData = async (
  { dashboardName },
  setFilterCategory,
  setFilterCountries,
  setFilterComapnies,
  setIsLoading,
  setData,
  { startDate, endDate },
  setNoData
) => {
  const requestBody = {
    dashboard_name: dashboardName,
    frame_work: "",
    start_date: startDate === "" ? new Date() : startDate,
    end_date: endDate === "" ? new Date() : endDate,
    company_id: null,
    countries: null,
  };
  try {
    setIsLoading(true);
    const response = await fetchPutResponse(
      "dashboard/fetch_dashboard_data",
      requestBody
    );
    if (!response.ok) throw new Error("Network response was not ok.");
    const { data: apiData } = await response.json();
    if (Object.keys(apiData).length !== 0) {
      setData(apiData);
      setFilterData(
        apiData,
        setFilterCategory,
        setFilterCountries,
        setFilterComapnies
      );
    } else {
      setNoData(true);
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    setIsLoading(false);
  }
};
export const fetchRelevanceData = async (
  { startDate, endDate }, // Destructure dateFilter for direct access to startDate and endDate
  setRelevanceData,
  { dashboardName }
) => {
  setRelevanceData(null);

  if (dashboardName !== "SANDGLASS_SASB_DASHBOARD") {
    console.log("Dashboard not applicable for relevance data fetch.");
    return;
  }
  const requestBody = {
    dashboard_name: "SANDGLASS_SASB_RELEVANCE_SCORE",
    frame_work: "",
    start_date: startDate || new Date(),
    end_date: endDate || new Date(),
    company_id: null,
    countries: null,
  };
  try {
    const response = await fetchPutResponse(
      "dashboard/fetch_relevance_data",
      requestBody
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const { data: apiData } = await response.json();
    setRelevanceData(apiData || null);
  } catch (error) {
    console.error("Failed to fetch relevance data:", error);
    setRelevanceData(null);
  }
};



export function hashString(key) {
  // Stable uint32 hash (FNV-1a style)
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function stableBaseColor(key) {
  const h = hashString(key);

  // Golden-angle hue spacing gives better spread than `% 360`
  const hue = (h * 137.508) % 360;

  // Keep saturation/lightness in a pleasant Highcharts range
  const sat = 62 + (h % 12);          // 62..73
  const light = 46 + ((h >>> 8) % 8); // 46..53

  return { hue, sat, light, _hash: h };
}

export function hueDistance(a, b) {
  const d = Math.abs(a - b);
  return Math.min(d, 360 - d);
}

/**
 * Make colors more distinct WITHOUT reassigning everyone.
 * - Colors are initially stable per key.
 * - If two hues are too close, ONLY the later one is nudged.
 * - Removing a series doesn't recolor others.
 *
 * Note: This is deterministic given the current series order.
 * If you want order-independent behavior, sort by key before nudging.
 */
export function applyLocalNudges(keys, { minHueGap = 18, maxNudges = 6 } = {}) {
  const colors = keys.map((k) => stableBaseColor(k));

  for (let i = 0; i < colors.length; i++) {
    let nudges = 0;

    while (
      nudges < maxNudges &&
      colors.slice(0, i).some((c) => hueDistance(colors[i].hue, c.hue) < minHueGap)
    ) {
      // Deterministic nudge: advance hue by golden angle
      colors[i].hue = (colors[i].hue + 137.508) % 360;

      // small L tweak to help separation even if hue still close
      colors[i].light = Math.min(58, colors[i].light + 2);

      nudges++;
    }
  }

  return colors.map((c) => `hsl(${c.hue.toFixed(1)}, ${c.sat}%, ${c.light}%)`);
}

// Optional helper if you want absolute stability even when order changes:
// Sort keys before nudging and then map back.
// (If you don't care about order changes, ignore this.)
export function applyLocalNudgesOrderIndependent(keys, opts) {
  const indexed = keys.map((k, i) => ({ k, i }));
  indexed.sort((a, b) => a.k.localeCompare(b.k));
  const sortedKeys = indexed.map((x) => x.k);
  const sortedColors = applyLocalNudges(sortedKeys, opts);
  const out = new Array(keys.length);
  for (let j = 0; j < indexed.length; j++) out[indexed[j].i] = sortedColors[j];
  return out;
}
